import { Button } from "@dropbox/dig-components/buttons";
import { Text, Title } from "@dropbox/dig-components/typography";
import { Box, Stack, useTheme } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { CheckmarkCircleFill, Twinkle2Fill } from "@dropbox/dig-icons/assets";
import { createUxaElementId } from "@mirage/analytics/uxa";
import i18n from "@mirage/translations";
import classnames from "classnames";
import React from "react";

import styles from "./PostInstallPage.module.css";

const assets = {
  light: {
    sidebar:
      "https://assets.dropbox.com/images/dashweb/growth/extension/sidebar.svg",
    aiShortcuts:
      "https://assets.dropbox.com/images/dashweb/growth/extension/ai_shortcuts.svg",
    stacks:
      "https://assets.dropbox.com/images/dashweb/growth/extension/stacks.svg",
    newTab:
      "https://assets.dropbox.com/images/dashweb/growth/extension/new_tab.svg",
    browserHistory:
      "https://assets.dropbox.com/images/dashweb/growth/extension/browser_history.svg",
  },
  dark: {
    sidebar:
      "https://assets.dropbox.com/images/dashweb/growth/extension/sidebar-dark.svg",
    aiShortcuts:
      "https://assets.dropbox.com/images/dashweb/growth/extension/ai_shortcuts-dark.svg",
    stacks:
      "https://assets.dropbox.com/images/dashweb/growth/extension/stacks-dark.svg",
    newTab:
      "https://assets.dropbox.com/images/dashweb/growth/extension/new_tab-dark.svg",
    browserHistory:
      "https://assets.dropbox.com/images/dashweb/growth/extension/browser_history-dark.svg",
  },
};

const UXA_ID = "post_extension_install_page";

export const PostInstallPage: React.FC = () => {
  const { mode } = useTheme();
  const images = assets[mode === "dark" ? "dark" : "light"];

  return (
    <>
      <Box display="flex" flexDirection="column" height="100%">
        <Box
          className={styles.banner}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <UIIcon
            color="var(--dig-color__success__base--state-1)"
            size="large"
            src={CheckmarkCircleFill}
          />
          <Text
            className={styles.success}
            variant="label"
            size="large"
            isBold
            tagName="p"
          >
            {i18n.t("extension_success_page_banner")}
          </Text>
        </Box>
        <Box className={styles.content} flexGrow={1}>
          <Box
            borderColor="Border Subtle"
            borderWidth="1"
            borderStyle="Solid"
            overflow="hidden"
            display="flex"
            className={classnames(styles.featureContainer, styles.padding0)}
          >
            <Box flexGrow={0}>
              <img
                src={images.sidebar}
                className={styles.sidebarImg}
                alt={i18n.t("extension_success_side_panel_img_alt")}
              />
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexGrow={1}
              className={styles.crossBgImg}
            >
              <Stack className={styles.sidePanelCopyContent}>
                <Title weightVariant="emphasized" size="large">
                  {i18n.t("extension_success_side_panel_content_title")}
                </Title>
                <Text size="xlarge" tagName="p">
                  {i18n.t("extension_success_side_panel_content_description")}
                </Text>
                <Button
                  className={styles.tryItBtn}
                  variant="primary"
                  size="xlarge"
                  withIconStart={<UIIcon src={Twinkle2Fill} />}
                  id="post-install-page-try-it-button"
                  data-uxa-log={createUxaElementId(UXA_ID, {
                    actionSurfaceComponent: UXA_ID,
                    featureLine: UXA_ID,
                  })}
                  data-uxa-interactions="shown click"
                >
                  {i18n.t("extension_success_side_panel_content_cta")}
                </Button>
              </Stack>
            </Box>
          </Box>
          <Box className={styles.gap48} display="flex" marginTop="Macro XLarge">
            <Stack gap="Macro Large">
              <Box
                className={classnames(styles.featureContainer, styles.darkblue)}
                borderColor="Border Subtle"
                borderWidth="1"
                borderStyle="Solid"
                borderRadius="Medium"
                overflow="hidden"
              >
                <div>
                  <Title
                    className={classnames(styles.noMarginTop, styles.white)}
                    weightVariant="emphasized"
                    size="large"
                  >
                    {i18n.t("extension_success_ask_content_title")}
                  </Title>
                  <Text className={styles.white} size="xlarge" tagName="p">
                    {i18n.t("extension_success_ask_content_description")}
                  </Text>
                </div>
                <img
                  className={styles.aiShortcutsImg}
                  src={images.aiShortcuts}
                  alt={i18n.t("extension_success_ask_img_alt")}
                />
              </Box>
              <Box
                className={classnames(
                  styles.featureContainer,
                  styles.lightblue,
                )}
                borderColor="Border Subtle"
                borderWidth="1"
                borderStyle="Solid"
                borderRadius="Medium"
                overflow="hidden"
                display="flex"
                flexDirection="column"
              >
                <div>
                  <Title
                    className={styles.noMarginTop}
                    weightVariant="emphasized"
                    size="large"
                  >
                    {i18n.t("extension_success_stacks_content_title")}
                  </Title>
                  <Text size="xlarge" tagName="p">
                    {i18n.t("extension_success_stacks_content_description")}
                  </Text>
                </div>
                <img
                  className={styles.stacksImg}
                  src={images.stacks}
                  alt={i18n.t("extension_success_stacks_img_alt")}
                />
              </Box>
            </Stack>
            <Stack gap="Macro Large">
              <Box
                className={classnames(
                  styles.featureContainer,
                  styles.lightblue,
                )}
                borderColor="Border Subtle"
                borderWidth="1"
                borderStyle="Solid"
                borderRadius="Medium"
                overflow="hidden"
              >
                <div>
                  <Title
                    className={styles.noMarginTop}
                    weightVariant="emphasized"
                    size="large"
                  >
                    {i18n.t("extension_success_new_tab_content_title")}
                  </Title>
                  <Text size="xlarge" tagName="p">
                    {i18n.t("extension_success_new_tab_content_description")}
                  </Text>
                </div>
                <img
                  className={styles.newTabImg}
                  src={images.newTab}
                  alt={i18n.t("extension_success_new_tab_img_alt")}
                />
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                className={classnames(styles.featureContainer, styles.darkblue)}
                borderColor="Border Subtle"
                borderWidth="1"
                borderStyle="Solid"
                borderRadius="Medium"
                overflow="hidden"
              >
                <div>
                  <Title
                    className={classnames(styles.noMarginTop, styles.white)}
                    weightVariant="emphasized"
                    size="large"
                  >
                    {i18n.t("extension_success_search_content_title")}
                  </Title>
                  <Text className={styles.white} size="xlarge" tagName="p">
                    {i18n.t("extension_success_search_content_description")}
                  </Text>
                </div>
                <img
                  className={styles.browserHistoryImg}
                  src={images.browserHistory}
                  alt={i18n.t("extension_success_search_img_alt")}
                />
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>
      {/* Content here is not visible to the user but used to help with the AI summary */}
      <div className={styles.hiddenContent}>
        AI-powered insights Access an AI-powered chatbot directly from a side
        panel on your webpage. You can ask questions about the page, extract key
        takeaways, and generate new AI-powered documents or responses without
        switching tabs. The Dash extension understands the content on the
        webpage and generates responses with that context — e.g. summarize and
        Q&A on dense content or creation of communications or new documents
        based on the webpage content. Choose from default button options like
        summarize, next steps, draft, spelling & grammar or create your own
        reusable prompts to speed up your workflows in the future. Browser
        history for search Your browser history is now searchable in Dash. Find
        work content you&apos;ve accessed recently in addition to all your
        connected content from one search bar. You&apos;ll now see the ability
        to filter search results on applications from your browser history. And
        don&apos;t worry, your browser history results are unique to your Dash
        experience, keeping them secure and private. New tab page Set Dash as
        your new tab page for quick access to search, organized content, and AI
        tools. Each time you open a new tab, you&apos;ll be directed to the Dash
        start page. Use the new tab as your launchpad throughout the day —
        retrieve information and content you need, stay in the know about
        activity on your content, prepare for upcoming meetings, and more! One
        click organization Found something useful that you want to save for
        later? Need to switch context but want to come back to your content
        later? The browser extension allows you to save your web content for
        quick access later. No more copy/paste to a to-do list or tab overload
        with items you want to come back to — just add to a stack with one
        click.
      </div>
    </>
  );
};
